import { useEffect, useMemo, useState } from "react";
import { getAgeGroup } from "src/utils/fhir";
import { getMonthsSince } from "src/utils/formatTime";
import { CustomReference } from "src/careflow/inck/nat-request/request-type";
import { TaskWrapper } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task";
import { PatientWrapper } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient";
import { RelatedPersonWrapper } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/RelatedPerson";
import { ServiceRequestWrapper, WrappedServiceRequest } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest";
import { useCodeSystems, usePatient, useRelatedPersons, useServiceRequests, useTasks } from "src/careflow/ph-core/fhir-client/fhir-react";
import useValueSetsByIdentifiers from "./useValueSetsByIdentifier";
import { ReferralService } from "src/crs/referral/services/ReferralService";

export const PLAN_DEFINITION_CREATE = "PlanDefinition/nat-outbound-referral-internal";

const useRequestValidation = ( patientId: string ): { 
  ageRangeValidation: boolean | null,
  planDefinition: any | null,
  ageRangePatient: any,
  requestActive: WrappedServiceRequest | null,
  relatedPersons: CustomReference[] | null,
  isLoading: boolean,
  handleRefreshValidation: any,
} => {
  const [planDefinition, setPlanDefinition] = useState<any[]>([]);
  const [isLoadingPlanDefinition, setIsLoadingPlanDefinition] = useState(true);

  const [ patient, { 
    refresh: refreshPatient, 
    isLoading: isLoadingPatient 
  } ] = usePatient(patientId, { map: PatientWrapper, autofetch: !!patientId });

  const [ serviceRequest, { 
    refresh: refreshServiceRequest, 
    isLoading: isLoadingServiceRequest 
  } ] = useServiceRequests({
    filter: {
      status: 'active,on-hold,draft',
      patient: patient?.id,
      _sort: '-authored',
    },
    map: ServiceRequestWrapper,
    autofetch: Boolean(patient),
  });

  const serviceRequestNatPD = useMemo(() => {
    if (!planDefinition?.length || !serviceRequest?.length) {
      return null;
    }
  
    const natPD = planDefinition.find(
      (e) =>
        e?.reference !== PLAN_DEFINITION_CREATE &&
        e?.planDefinitionIdentifierValue
    );
  
    if (!natPD?.reference) {
      return null;
    }

    const matchedServiceRequest = serviceRequest.find((s) =>
      s?.instantiatesCanonical?.includes(natPD.reference)
    );
  
    return matchedServiceRequest ?? null;
  }, [planDefinition, serviceRequest]);

  const [ codeSystemAgeRange, { isLoading: isLoadingCS } ] = useCodeSystems({
    filter: { name: 'Needs Assesment Tool Age Groups' }
  });

  const {
    valueSets: [
      requestIntentVS,
      referralServicesInternal,
      referralStatus
    ], isLoading: isLoadingVS
  } = useValueSetsByIdentifiers([
    'request-intent',
    'ph-referral-services-internal',
    'service-request-status'
  ]);

  const patientAgeRange = useMemo(() => 
    patient?.birthDate && getAgeGroup(getMonthsSince(new Date(patient.birthDate)), codeSystemAgeRange?.[0])
  , [patient, codeSystemAgeRange]);

  useEffect(() => {
    const fetchPlanDefinition = async () => {
      setIsLoadingPlanDefinition(true);
      try {
        const mapPlanActivityDefinition = 
          await ReferralService.getPlanActivityDefinition(requestIntentVS, referralServicesInternal, referralStatus);
        const patientAgeRangeCode: any = patientAgeRange?.[0];
        const getPlanDefinition = mapPlanActivityDefinition?.
          filter((e) => 
            e?.reference === PLAN_DEFINITION_CREATE || 
          e?.planDefinitionIdentifierValue === patientAgeRangeCode?.code);
        setPlanDefinition(getPlanDefinition);
      } catch (error) {
        console.error("Error fetching PlanDefinition:", error);
      } finally {
        setIsLoadingPlanDefinition(false);
      }
    };
    
    if (requestIntentVS && referralServicesInternal) {
      fetchPlanDefinition();
    }
  }, [patient, requestIntentVS, referralServicesInternal,patientAgeRange]);

  const [relatedPersons, { 
    refresh: refreshRelatedPersons, 
    isLoading: isLoadingRelatedPersons 
  }] = useRelatedPersons({
    filter: { patient: patient?.id, active: true },
    map: RelatedPersonWrapper,
    autofetch: !!patient,
  });

  const [tasks, { refresh: refreshTasks, isFetching: isLoadingTasks }] = useTasks({
    filter: {
      code: 'completed-nat-assessment',
      'based-on': serviceRequestNatPD?.id,
      status: 'requested,received,accepted,ready,in-progress,on-hold,draft',
      _sort: '-authored-on'
    },
    map: TaskWrapper,
    autofetch: Boolean(serviceRequestNatPD)
  });

  const owners: CustomReference[] = useMemo(() => {
    if (!relatedPersons || !tasks || !patient) return [];

    return relatedPersons.map(x1 => ({
        reference: `RelatedPerson/${x1.id}`,
        display: x1?.getFullName() ?? undefined,
        relationship: x1?.relationship?.[0]?.text ?? x1?.relationship?.[0]?.coding?.[0]?.display ?? undefined,
        contact: x1?.getPrimaryEmail()?.value || x1?.getPrimaryPhone()?.value ? true : false,
        tasks: tasks?.filter(e => e?.owner?.reference === `RelatedPerson/${x1.id}`)
    })).concat({
      reference: `Patient/${patient?.id}`,
      display: patient?.getFullName() ?? undefined,
      relationship: patient?.resourceType,
      contact: patient?.getPrimaryEmail()?.value || patient?.getPrimaryPhone()?.value ? true : false,
      tasks: tasks?.filter(e => e?.owner?.reference === `Patient/${patient?.id}`)
    });
  }, [relatedPersons, tasks, patient]);

  const planDefinitionCorrespondsToActiveRequest = useMemo(() => {
    if (Boolean(serviceRequestNatPD) && planDefinition?.length > 0) {
      const instantiatesCanonicals = serviceRequestNatPD?.instantiatesCanonical ?? [];
      return planDefinition.some((pd) =>
        instantiatesCanonicals.includes(pd?.reference)
      );
    }
    return false;
  }, [serviceRequestNatPD, planDefinition]);

  const isLoading = useMemo(() => 
    isLoadingPatient || 
    isLoadingServiceRequest || 
    isLoadingPlanDefinition || 
    isLoadingRelatedPersons || 
    isLoadingTasks ||
    isLoadingVS ||
    isLoadingCS, 
  [
    isLoadingPatient, 
    isLoadingServiceRequest, 
    isLoadingPlanDefinition, 
    isLoadingRelatedPersons, 
    isLoadingTasks,
    isLoadingVS,
    isLoadingCS
  ]);

  const handleRefreshValidation = () => {
    refreshPatient();
    refreshServiceRequest();
    refreshRelatedPersons();
    refreshTasks();
  };

  const responseValidation = useMemo(() => {
    if (isLoading) {
      return {
        ageRangeValidation: null,
        planDefinition: null,
        ageRangePatient: null,
        requestActive: null,
        relatedPersons: null,
        isLoading: true,
        handleRefreshValidation
      };
    }

    return {
      ageRangeValidation: planDefinitionCorrespondsToActiveRequest ?? null,
      planDefinition: planDefinition ?? null,
      ageRangePatient: patientAgeRange?.[0] ?? null,
      requestActive: serviceRequestNatPD ?? null,
      relatedPersons: owners,
      isLoading: false,
      handleRefreshValidation
    };
  }, [patient, isLoading, planDefinitionCorrespondsToActiveRequest, planDefinition, patientAgeRange, serviceRequestNatPD, owners]);

  return responseValidation;
};

export default useRequestValidation;